import { useMemo, useCallback } from "react";
import type { BaseEntity, SimpleTableConfig } from "../../../types";
import type {
  BulkActionWithCallback,
  BulkActionWithOnClick,
} from "../../../../BulkActions/types";

interface MemoizationResult<TData extends BaseEntity> {
  selectedItemsArray: TData[];
  allItemIds: string[];
  handleClearSelection: () => void;
  handleSelectAllItems: () => void;
  customActions: BulkActionWithCallback<TData>[];
}

/**
 * Handles all memoization logic for SimpleTableDesktopView
 * Prevents unnecessary re-renders by memoizing computed values and callbacks
 */
export function useSimpleTableMemoization<TData extends BaseEntity>(
  paginatedData: TData[],
  selectedItems: Set<string>,
  setSelectedItems: (
    items: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void,
  config: SimpleTableConfig<TData>,
  onBulkAction?: (actionId: string, selectedItems: TData[]) => void
): MemoizationResult<TData> {
  // Memoize selected items array to prevent recreation on every render
  const selectedItemsArray = useMemo(() => {
    return paginatedData.filter((item) => selectedItems.has(item.id));
  }, [paginatedData, selectedItems]);

  // Memoize all item IDs array
  const allItemIds = useMemo(() => {
    return paginatedData.map((item) => item.id);
  }, [paginatedData]);

  // Memoize clear selection handler
  const handleClearSelection = useCallback(() => {
    setSelectedItems(new Set());
  }, [setSelectedItems]);

  // Memoize select all handler
  const handleSelectAllItems = useCallback(() => {
    setSelectedItems(new Set(allItemIds));
  }, [setSelectedItems, allItemIds]);

  // Memoize custom actions to prevent recreation on every render.
  //
  // `config.bulkActions` entries declare `onClick` as OPTIONAL; `GroupedBulkActions`
  // requires it. This is the adapter: a declared `onClick` passes through, and
  // anything without one is wrapped so the table's `onBulkAction` receives it.
  // Typing the parameter (it was `config: any`, with `action: any` inside and an
  // `as BulkActionWithCallback<TData>[]` on the way out) makes both halves of that
  // contract checked — main-03.
  const customActions = useMemo<BulkActionWithCallback<TData>[]>(() => {
    const declaredActions = config.bulkActions ?? [];

    return declaredActions.map((action): BulkActionWithOnClick<TData> => {
      const icon = action.icon || "⚙️"; // Default icon if the config omits one
      const { id, label, variant } = action;

      // Custom action: it brought its own handler, so call it directly.
      if (action.onClick) {
        return { id, label, icon, variant, onClick: action.onClick };
      }

      // Standard action: route it back through the table's bulk-action handler.
      return {
        id,
        label,
        icon,
        variant,
        onClick: async (selectedBulkItems: TData[]) => {
          onBulkAction?.(id, selectedBulkItems);
        },
      };
    });
  }, [config.bulkActions, onBulkAction]);

  return {
    selectedItemsArray,
    allItemIds,
    handleClearSelection,
    handleSelectAllItems,
    customActions,
  };
}
